import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Grid,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
  Box,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { debounce } from "throttle-debounce";
import DeleteIcon from "@material-ui/icons/Delete";
import { useForm } from "react-hook-form";
import { MIcon, TableV2, TextFieldV2 } from "@cobee/cobee-ui";
import useGetCompany from "v2/ui/hooks/common/useGetCompany";
import { translate as t } from "../../../../../infrastructure/translate/localization";
import Employee from "../../../../domain/Employee";
import useStyles from "./styles";
import CobeeButton from "../../../../components/CobeeButton";
import searchInKeys from "../../../../../services/searchInKeys";
import ModalContext from "../../../../../infrastructure/Contexts/components/ModalWrapper/context";
import EmployeeDetailsModal from "../../../../components/modals/employee/EmployeeDetailsModal";
import ActionConfirmationModal from "../../../../components/modals/ActionConfirmationModal";
import EmployeeActionsContext from "../../../../../infrastructure/Contexts/components/actions/EmployeeActionsWrapper/context";
import { showErrorMessage } from "../../../../../usecases/commons/show-error-message";
import { headings, keys, getColumns } from "./config";
import { CountryIsoCode } from "../../../../../v2/domain/common/enums/CountryISOCode";

const TextMessage = ({
  type,
  countryIsoCode,
  children,
}: PropsWithChildren<{
  type: "error" | "warning";
  countryIsoCode?: CountryIsoCode;
}>) => {
  const companyCountryIsoCode = countryIsoCode?.toLowerCase();
  const translationPath = `employees-section.${children}`;

  return (
    <Box display="flex" alignItems="center">
      <MIcon.FiberManualRecord
        style={{
          color: type === "error" ? "red" : "orange",
          fontSize: 8,
          marginRight: 8,
        }}
      />
      <Typography variant="caption">
        {companyCountryIsoCode
          ? t(`${translationPath}-${companyCountryIsoCode}`) ||
            t(translationPath)
          : t(translationPath)}
      </Typography>
    </Box>
  );
};

export default function EmployeeCategoryTable({
  data,
  category,
  isLoading,
}: {
  data: Employee[];
  category: string;
  isLoading: boolean;
}) {
  const {
    searchIcon,
    searchField,
    removeButton,

    progressContainer,
  } = useStyles();

  const [search, setSearch] = useState("");
  const { data: company } = useGetCompany();

  const { openModal } = useContext(ModalContext);

  const { deleteEmployeesByIdIsLoading } = useContext(EmployeeActionsContext);

  const {
    deleteEmployeesById,
    modifyEmployeesBenefitPlan,
    modifyEmployeesTaxRegime,
    inviteUploadingEmployees,
  } = useContext(EmployeeActionsContext);

  const { register, getValues, watch, setValue } = useForm();

  useEffect(() => {
    register({ name: "selected", value: [] });
    register({ name: "currentPage", value: 0 });
  }, [register]);

  const handleSearch = debounce(1000, false, (value) => {
    setSearch(value);
    setValue("currentPage", 0);
  });

  const searchResults = useCallback(
    (employees) => {
      return searchInKeys({ search, items: employees, keys });
    },
    [search]
  );

  const canInvite = () => {
    const values = getValues();
    const employeesToInvite = values.selected.map((key) =>
      data.find(({ id }) => id === key)
    );
    const benefitPlanOfFirst = employeesToInvite?.[0]?.modelId;
    return (
      employeesToInvite.length > 0 &&
      employeesToInvite.every(({ modelId }) => modelId === benefitPlanOfFirst)
    );
  };

  const handleInvite = () => {
    if (!canInvite()) {
      showErrorMessage(t("employees-section.error-employees-invite"));
    } else {
      const values = getValues();
      const employeeIds = values.selected;
      const employees = data.filter(({ id }) =>
        employeeIds.some((employeeId) => employeeId === id)
      );
      const benefitPlanId = employees[0].modelId;
      inviteUploadingEmployees({
        employees,
        benefitPlanId,
        sideEffect: () => {
          setValue("selected", []);
        },
      });
      setValue("currentPage", 0);
    }
  };

  const someAreSelected = watch("selected")?.length > 0;

  if (!data.length) return <></>;

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography variant="h6">{`${headings[category]}`}</Typography>
        <TextFieldV2
          label={t("actions.search")}
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon className={searchIcon} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box mb={2} display="flex" alignItems="center">
        <Box>
          <IconButton
            size="small"
            className={removeButton}
            disabled={!someAreSelected}
            onClick={() => {
              if (someAreSelected) {
                openModal({
                  title: t("employees-section.delete-employees"),
                  modalContents: (
                    <ActionConfirmationModal
                      withoutWrapper
                      title={t("employees-section.delete-employees")}
                      message={t(
                        "employees-section.delete-invited-employee-sure"
                      )}
                      callback={() => {
                        const values = getValues();
                        deleteEmployeesById({
                          employeeIds: values.selected,
                          sideEffect: () => {
                            setValue("selected", []);
                            setValue("currentPage", 0);
                          },
                        });
                      }}
                    />
                  ),
                });
              }
            }}
          >
            <DeleteIcon />
            {t("edit-employees.remove")}
          </IconButton>
        </Box>
      </Box>
      {isLoading || deleteEmployeesByIdIsLoading ? (
        <Grid
          item
          container
          xs={12}
          justifyContent="center"
          alignItems="center"
          className={progressContainer}
        >
          <CircularProgress color="secondary" />
        </Grid>
      ) : (
        <>
          <TableV2
            forcePagination
            pageNumber={watch("currentPage")}
            onChange={(value) => setValue("currentPage", value - 1)}
            withMultiSelect
            defaultSelected={watch("selected")}
            setSelectedItems={(value) => setValue("selected", value)}
            testId={`employee-category-table-${category}`}
            columns={getColumns({ category })}
            rows={searchResults(data).map(
              ({ id, internalId, modelId, warning, error, ...rest }) => {
                const warnings = warning?.split(",");
                const errors = error?.split(",");

                return {
                  warning: warnings?.map(
                    (item) =>
                      item && (
                        <TextMessage
                          type="warning"
                          countryIsoCode={company?.countryIsoCode}
                        >
                          {item}
                        </TextMessage>
                      )
                  ),
                  error: errors?.map(
                    (item) =>
                      item && (
                        <TextMessage
                          type="error"
                          countryIsoCode={company?.countryIsoCode}
                        >
                          {item}
                        </TextMessage>
                      )
                  ),
                  internalId: internalId === "" ? "-" : internalId,
                  ...rest,
                  id,
                  actions: (
                    <CobeeButton
                      styleType="uncontained"
                      onClick={() => {
                        openModal({
                          title: t("edit-employees.edit-employee"),
                          modalContents: (
                            <EmployeeDetailsModal
                              defaultValues={{
                                internalId,
                                id,
                                benefitPlan: modelId,
                                ...rest,
                              }}
                            />
                          ),
                        });
                      }}
                    >
                      {t("actions.edit")}
                    </CobeeButton>
                  ),
                };
              }
            )}
          />
          {category !== "error" && (
            <Box mt={3} width="100%" textAlign="right">
              <CobeeButton
                styleType="primary"
                disabled={watch("selected").length === 0}
                onClick={handleInvite}
              >
                <Typography variant="body2">{t("actions.invite")}</Typography>
              </CobeeButton>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
